import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { EASE, Reveal } from "@/components/Reveal";
import { NeonButton, GhostButton } from "@/components/Buttons";
import { Terminal } from "@/components/Terminal";
import { scrollToId } from "@/lib/ui";

function MaskLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const ECOSYSTEM = [
  "P.E.E. GAME PLAN",
  "P.E.E. OS",
  "EXECUTOR TRAINING",
  "ANALYTICS & REVIEW",
  "EXECUTOR DEVELOPMENT",
];

export default function HeroV2() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yTerm = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const opacityTerm = useTransform(scrollYProgress, [0, 0.85], [1, 0.35]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section-v2"
      className="relative overflow-hidden pt-32 sm:pt-40"
    >
      <div className="pointer-events-none absolute -top-40 right-[-15%] h-[560px] w-[560px] rounded-full bg-neon/[0.05] blur-[140px]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon/40 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-neon"
        >
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-neon" />
          P.E.E. OS™ — Execution Traders Community
        </motion.div>

        <h1
          data-testid="hero-headline-v2"
          className="font-display text-[2.6rem] font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <MaskLine delay={0.2}>Stop chasing trades.</MaskLine>
          <MaskLine delay={0.34}>Start executing</MaskLine>
          <MaskLine delay={0.48}>
            a <span className="glow-text-neon text-neon">game plan.</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-mist"
        >
          Most traders don't need more noise, more predictions, or another
          collection of random setups. They need a defined trading methodology,
          a process for executing it, and the discipline to follow it
          consistently. P.E.E. combines the Game Plan, the OS, training,
          analytics, and community into one execution-focused ecosystem.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-mist">
            Position • Event • Entry
          </div>
          <div className="mt-3 font-display text-xl font-bold uppercase tracking-wide text-white sm:text-2xl">
            Execution Over Everything.™
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <NeonButton
            testid="hero-v2-cta-primary"
            onClick={() => scrollToId("framework")}
          >
            See How P.E.E. Works
            <ArrowRight size={16} />
          </NeonButton>
          <GhostButton
            testid="hero-v2-cta-secondary"
            onClick={() => scrollToId("pricing")}
          >
            Join Execution Traders
            <ArrowDown size={16} />
          </GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-dim"
        >
          <span className="h-px w-10 bg-line" />
          No guaranteed outcomes. No hype. A defined process built around execution.
        </motion.div>

        <div className="mt-16 sm:mt-20" style={{ perspective: "1400px" }}>
          <motion.div
            style={{ y: yTerm, opacity: opacityTerm }}
            initial={{ opacity: 0, y: 70, rotateX: 9 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 1.3, delay: 0.65, ease: EASE }}
          >
            <Terminal />
          </motion.div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="ecosystem-strip"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-t border-line py-10 text-center"
          >
            {ECOSYSTEM.map((e, i) => (
              <span key={e} className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist sm:text-[11px]">
                  {e}
                </span>
                {i < ECOSYSTEM.length - 1 && (
                  <span className="font-mono text-xs text-neon">→</span>
                )}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
